import {
  Alert,
  Button,
  Link,
  Code as OrbitCode,
  Spinner,
  StatusBadge,
} from '@apollo/orbit';
import React from 'react';

import { Code } from 'src/components/code/Code';
import { DocsRouter } from 'src/lib/routers';
import {
  introspectionToSubgraphPublish,
  schemaFileToSubgraphPublish,
} from 'src/lib/roverCommands';

import { FederationGettingStartedInstructions } from '../publishUsingFederation/PublishUsingFederation';

export const FederationTab = ({
  firstPublish,
  graphId,
  graphVariant,
  serviceApiToken,
  hasBuildErrors,
  onClose,
}: {
  firstPublish: boolean;
  graphId: string;
  graphVariant: string;
  serviceApiToken: string;
  hasBuildErrors: boolean;
  onClose?: () => void;
}) => {
  const fileSchemaScript = schemaFileToSubgraphPublish({
    apiKey: firstPublish ? serviceApiToken : undefined,
    graphId,
    graphVariant,
    schemaLocation: './products-schema.graphql',
    routingUrl: 'http://products.prod.svc.cluster.local:4001/graphql',
    highlightGraphRef: false,
  });

  const introspectSchemaScript = introspectionToSubgraphPublish({
    apiKey: firstPublish ? serviceApiToken : undefined,
    graphId,
    graphVariant,
    highlightGraphRef: false,
  });

  return (
    <>
      {hasBuildErrors && (
        <Alert status="error" className="mb-4">
          {`Your most recent subgraph pushes have caused supergraph schema build errors. ${
            firstPublish
              ? `To explore your schema, supergraph schema build needs to succeed. Resolve all subgraph composition errors and push again.`
              : `Any of these recent updates to your subgraph will not be reflected in your Schema documentation.`
          }`}
        </Alert>
      )}
      <p className="mb-4">
        {firstPublish ? (
          <>
            Pick this option to set up a graph that uses{' '}
            <Link isExternal to={DocsRouter.path('Federation')}>
              Apollo Federation
            </Link>
            .{' '}
          </>
        ) : (
          <>Push a subgraph to your federated graph. </>
        )}
        As you register each subgraph, Apollo will attempt to compose all your
        subgraphs into a single supergraph schema. Whenever supergraph schema
        build succeeds, your router can fetch the latest federated schema from
        the registry.
      </p>
      <FederationGettingStartedInstructions firstPublish={firstPublish} />
      <p className="pt-4">
        <b>Option 1:</b> Use a local file containing the subgraph schema (e.g.,
        called <OrbitCode>products-schema.graphql</OrbitCode>)
      </p>
      <Code
        title="products-schema.graphql"
        className="mt-2"
        showCopyButton={true}
        firstLineDecorator="$ "
        highlight={fileSchemaScript.shellStringLinesToHighlight}
        mask={serviceApiToken.split(':').pop()}
        code={fileSchemaScript.shellString}
      />
      <p className="pt-4">
        <b>Option 2:</b> Introspect a running subgraph (e.g., at{' '}
        <OrbitCode>http://localhost:4001</OrbitCode>)
      </p>
      <Code
        className="mt-2"
        showCopyButton={true}
        firstLineDecorator="$ "
        highlight={introspectSchemaScript.shellStringLinesToHighlight}
        mask={serviceApiToken.split(':').pop()}
        code={introspectSchemaScript.shellString}
      />
      <Alert status="info" className="mt-4">
        Visit the docs on{' '}
        <Link isExternal to={DocsRouter.path('ManagedFederation')}>
          setting up managed federation
        </Link>{' '}
        for an overview of how this works and full details on the setup. If
        you're still using the <OrbitCode>apollo</OrbitCode> CLI, see the
        instructions for{' '}
        <Link
          isExternal
          className="text-nowrap"
          to="https://www.npmjs.com/package/apollo/v/2.33.1#apollo-servicepush"
        >
          <OrbitCode>apollo service:push</OrbitCode>
        </Link>{' '}
        command.
      </Alert>
      <div className="mt-8 flex items-end">
        {firstPublish && (
          <StatusBadge icon={<Spinner size="xs" />}>
            Awaiting schema
          </StatusBadge>
        )}
        {onClose && (
          <div className="flex flex-1 justify-end">
            <Button
              variant="primary"
              type="submit"
              className="flex-none"
              onClick={onClose}
            >
              Done
            </Button>
          </div>
        )}
      </div>
    </>
  );
};
